import { useCallback, useEffect, useRef, useState } from 'react';
import { createLatestRequestGate, patchRow } from '../definitions';

export const PAGE_SIZE = 10;

/**
 * The table's view (page, sort, search) is kept in sessionStorage so it survives the
 * component unmounting, which is what happens whenever an admin opens an editor page.
 *
 * Wrapped because `sessionStorage` is not there in every environment the tests run in,
 * and a private window can throw on access rather than returning null. A failure here
 * must never stop a list rendering, so both helpers fall back to "no saved view".
 */
/**
 * SURF-4802/4805/4806: bumping this version orphans every view saved under the old key.
 * The saved view used to include the sort, so a tab that had opened a list while the
 * default was still `title` kept restoring `title` forever, and the switch to Last
 * Modified newest-first never took effect for that tab. A newly added row then sorted
 * alphabetically, which is exactly what QA filed. Any change to a default sort needs the
 * same bump, or the saved view hides it.
 */
const viewKey = (stateKey) => `resourceTable:v2:${stateKey}`;

const restoreView = (stateKey) => {
  if (!stateKey) return {};
  try {
    return JSON.parse(sessionStorage.getItem(viewKey(stateKey))) || {};
  } catch {
    return {};
  }
};

const saveView = (stateKey, view) => {
  if (!stateKey) return;
  try {
    sessionStorage.setItem(viewKey(stateKey), JSON.stringify(view));
  } catch {
    // Storage unavailable or full. The table still works, it just forgets.
  }
};

/**
 * Drop the remembered page and sort for a table, keeping its search.
 *
 * Called when an admin starts creating something. Remembering the view is right when
 * they come back from editing a row, and wrong when they come back from adding one: the
 * new row belongs at the top of page one under Last Modified newest-first, so returning
 * to page four, or to a Title sort, hides the thing they just made. The search is kept
 * because a filter is a visible, deliberate choice; a sort left over from an earlier
 * session is not. The pages that open a separate editor screen call this as they
 * navigate, because by the time the list mounts again it has no way to know why.
 */
export const forgetSavedPage = (stateKey) => {
  if (!stateKey) return;
  try {
    const saved = JSON.parse(sessionStorage.getItem(viewKey(stateKey))) || {};
    // With no saved sort, the mount falls through to the defaults.
    const kept = { ...saved, start: 0 };
    delete kept.sortPredicate;
    delete kept.sortReverse;
    sessionStorage.setItem(viewKey(stateKey), JSON.stringify(kept));
  } catch {
    // Nothing remembered, or storage is unavailable. Either way the list opens on page one.
  }
};

/**
 * SURF-4843: forget every table's saved view (page, sort and search).
 *
 * The rail calls this before it opens a screen. A list opened from the rail starts on
 * page one with its default sort and no search; a list the admin comes back to from one
 * of its own records (an editor's Back, Cancel or Save) is where they left it. That is
 * the rule the `CmsItemListSection` lists already follow, and without this the lists on
 * this hook broke it: leaving External Url on page three for Calendar and clicking back
 * reopened page three, because the view outlived the visit.
 *
 * Everything under the prefix goes, because the rail cannot know which table the next
 * screen shows, and a view saved by a screen the admin has left is stale by definition.
 */
export const forgetSavedViews = () => {
  const prefix = viewKey('');
  try {
    const keys = [];
    for (let index = 0; index < sessionStorage.length; index += 1) {
      const key = sessionStorage.key(index);
      if (key && key.startsWith(prefix)) keys.push(key);
    }
    keys.forEach((key) => sessionStorage.removeItem(key));
  } catch {
    // Storage unavailable, so nothing was saved and every list opens on page one anyway.
  }
};

/**
 * Server-paginated table state: page, sort and search, plus the fetch that reacts to
 * them. Replaces smart-table's `st-pipe` loop.
 *
 * Search is debounced so typing does not fire a request per keystroke, and any change to
 * sort or search resets to the first page; otherwise you can land on page 4 of a
 * one-page result and see nothing.
 *
 * `initialSortPredicate` / `initialSortReverse` set the column the table opens on, and
 * both go straight into the request.
 *
 * The predicate becomes an ORDER BY column name on the server, so only a real column may
 * be passed: `title`, `type`, `sourceTenantId` or `updated` on the resource lists. The
 * default is 'title', which is what those lists declared with `st-sort-default`, and the
 * three of them (External Url, Pdf, WYSIWYG) now open on `updated` descending so a page
 * you just created is at the top. Editing does not restamp `updated` (SURF-4794), which
 * is what lets an edited row stay on the page the admin came back to.
 *
 * A table whose Angular header had no default sort can pass '' instead, so no arrow is
 * drawn and the server applies its own order until a header is clicked. Resource
 * Categories must not use `updated`: its sortPredicate reaches a table with no timestamp
 * column, so it opens on `id` descending instead (SURF-4817).
 *
 * `extraKey` is for whatever else narrows the result set: a value the hook cannot see,
 * because it lives in the caller's own state and is applied inside its `fetchPage`.
 * Passing it down as a string is what resets to page one when that value changes, the
 * same as a search or a sort does. The Directories pages hand down the directory or
 * division they are scoped to. The resource lists and Resource Categories pass nothing,
 * because SURF-4794 and SURF-4816 dropped their tag filters, so read from one of those
 * this looks dead. It is not: removing it strands the callers above on a page their new
 * result set may not have, with no error to show for it.
 */
const usePageTable = ({
  fetchPage,
  extraKey,
  enabled = true,
  onError,
  initialSortPredicate = 'title',
  initialSortReverse = false,
  stateKey,
}) => {
  const saved = restoreView(stateKey);
  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [start, setStart] = useState(saved.start ?? 0);
  const [sortPredicate, setSortPredicate] = useState(saved.sortPredicate ?? initialSortPredicate);
  const [sortReverse, setSortReverse] = useState(saved.sortReverse ?? initialSortReverse);
  const [searchInput, setSearchInput] = useState(saved.searchInput ?? '');
  const [searchPredicate, setSearchPredicate] = useState(saved.searchInput ?? '');
  const [loading, setLoading] = useState(false);
  /* False until a request has settled. The fetch starts in an effect, so without this the
     first render (and the one in which `enabled` turns on) reads as "loaded, none found"
     and the table shows its empty message before the request has even gone out. */
  const [loaded, setLoaded] = useState(false);
  const mountedRef = useRef(true);
  // The reset-to-page-one effect below must not fire on the first render, or it would
  // immediately throw away the page we just restored.
  const restoredRef = useRef(true);
  // SURF-4803, see `createLatestRequestGate`. Two fetches can be in flight at once when
  // the tag filter changes; only the newest may write rows.
  const gateRef = useRef(null);
  if (gateRef.current === null) gateRef.current = createLatestRequestGate();
  // Lets the reset effect read the current page without taking `start` as a dependency,
  // which would make it re-run and reset the page on every page turn.
  const startRef = useRef(start);
  // Set when the view changed while the admin was past page one. See the reset effect.
  const skipStaleFetchRef = useRef(false);

  useEffect(() => {
    startRef.current = start;
  }, [start]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // SURF-4801: editing an item navigates away, which unmounts the list, so on return the
  // admin landed on page one of an unfiltered list. Remembering the view means they come
  // back to where they were.
  useEffect(() => {
    saveView(stateKey, { start, sortPredicate, sortReverse, searchInput });
  }, [stateKey, start, sortPredicate, sortReverse, searchInput]);

  useEffect(() => {
    const timer = setTimeout(() => setSearchPredicate(searchInput), 250);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // Changing the search, the sort or a caller's own filter (`extraKey`) starts a
  // different result set, so page four of the old one is meaningless. Skipped once on
  // mount so a restored view is not discarded before it is used.
  useEffect(() => {
    if (restoredRef.current) {
      restoredRef.current = false;
      return;
    }
    // SURF-4803: the fetch effect below runs in this same commit and still closes over
    // the old page, so a narrowing search or tag filter would ask for page four of a
    // one-page result and get nothing back. Skip that request and let the reset drive
    // the real one.
    if (startRef.current !== 0) skipStaleFetchRef.current = true;
    setStart(0);
  }, [searchPredicate, sortPredicate, sortReverse, extraKey]);

  const load = useCallback(async () => {
    if (!enabled) return;
    const ticket = gateRef.current.take();
    setLoading(true);
    try {
      const result = await fetchPage({
        start,
        number: PAGE_SIZE,
        sortPredicate,
        sortReverse,
        searchPredicate: searchPredicate || undefined,
      });
      // A response that a newer request has already superseded must not write rows:
      // that is what left the table blank after a tag was picked (SURF-4803).
      if (!mountedRef.current || !gateRef.current.isLatest(ticket)) return;
      setRows(result.pages);
      setCount(result.count);
      // Removing the only row on the last page leaves `start` past the end, which would
      // show an empty table with no way back. Step to whatever the last real page is now.
      const lastStart = Math.max(0, (Math.ceil(result.count / PAGE_SIZE) - 1) * PAGE_SIZE);
      if (start > lastStart) setStart(lastStart);
    } catch (error) {
      if (!gateRef.current.isLatest(ticket)) return;
      if (onError) onError(error);
    } finally {
      if (mountedRef.current && gateRef.current.isLatest(ticket)) {
        setLoading(false);
        setLoaded(true);
      }
    }
  }, [enabled, fetchPage, onError, searchPredicate, sortPredicate, sortReverse, start]);

  useEffect(() => {
    if (skipStaleFetchRef.current) {
      skipStaleFetchRef.current = false;
      return;
    }
    load();
  }, [load]);

  const toggleSort = useCallback(
    (predicate) => {
      if (predicate === sortPredicate) setSortReverse((current) => !current);
      else {
        setSortPredicate(predicate);
        setSortReverse(false);
      }
    },
    [sortPredicate]
  );

  /**
   * SURF-4841: change one row on the page without fetching.
   *
   * For Publish and Unpublish. The server restamps Last Modified on both, so re-fetching
   * the page under the default Last Modified newest-first sort moved the row to the top
   * of page one, and it vanished from the page the admin was on. Editing does not
   * restamp, which is why an edited row stayed put and a published one did not. The row
   * keeps its place and its old Last Modified until the next fetch.
   */
  const updateRow = useCallback((id, patch) => {
    setRows((current) => patchRow(current, id, patch));
  }, []);

  const numberOfPages = Math.max(1, Math.ceil(count / PAGE_SIZE));
  const currentPage = Math.floor(start / PAGE_SIZE) + 1;

  return {
    rows,
    count,
    // Waiting for data: a request in flight, or none settled yet. A disabled table waits
    // for nothing.
    loading: enabled && (loading || !loaded),
    searchInput,
    setSearchInput,
    sortPredicate,
    sortReverse,
    toggleSort,
    numberOfPages,
    currentPage,
    goToPage: (page) => setStart((page - 1) * PAGE_SIZE),
    reload: load,
    /**
     * Re-fetch the page the admin is on. This is what a delete should do (SURF-4801):
     * `load` keeps `start`, and the clamp above handles the case where the row removed
     * was the last one on the last page.
     */
    reloadCurrentPage: load,
    updateRow,
    /** Jump back to page one. For an add, where the new row is not on the current page. */
    reloadFirstPage: () => (start === 0 ? load() : setStart(0)),
    /**
     * Page one, default sort. For an add on a page whose editor is a modal, so the list
     * never unmounted and `forgetSavedPage` had nothing to act on (SURF-4805). Under the
     * default of Last Modified newest-first that puts the new row at the top. When the
     * sort is already the default the reset effect has nothing to react to, so the fetch
     * is driven directly, exactly as `reloadFirstPage` does.
     */
    resetToDefaultView: () => {
      const sortIsDefault =
        sortPredicate === initialSortPredicate && sortReverse === initialSortReverse;
      setSortPredicate(initialSortPredicate);
      setSortReverse(initialSortReverse);
      if (sortIsDefault) {
        if (start === 0) load();
        else setStart(0);
      }
    },
  };
};

export default usePageTable;
